/**
 * Date utility type definitions
 */

/**
 * Date field mapping for format parsing
 */
export const DATE_FIELD_MAP: Record<string, string> = {
    "G": "era",
    "y": "year",
    "q": "quarter",
    "Q": "quarter",
    "M": "month",
    "L": "month",
    "d": "day",
    "E": "weekday",
    "c": "weekday",
    "e": "weekday",
    "h": "hour",
    "H": "hour",
    "k": "hour",
    "K": "hour",
    "m": "minute",
    "s": "second",
    "a": "dayperiod",
    "t": "dayperiod",
    "x": "zone",
    "X": "zone",
    "z": "zone",
    "Z": "zone"
};

/**
 * Name type configuration for date parts
 */
export interface NameTypeConfig {
    type: string;
    minLength: number | Record<string, number>;
    standAlone?: string;
}

/**
 * Name types for date formatting
 */
export const NAME_TYPES: Record<string, NameTypeConfig> = {
    month: {
        type: "months",
        minLength: 3,
        standAlone: "L"
    },
    quarter: {
        type: "quarters",
        minLength: 3,
        standAlone: "q"
    },
    weekday: {
        type: "days",
        minLength: {
            E: 0,
            c: 3,
            e: 3
        },
        standAlone: "c"
    },
    dayperiod: {
        type: "dayPeriods",
        minLength: 0
    },
    era: {
        type: "eras",
        minLength: 0
    }
};

/**
 * Date format part structure
 */
export interface DateFormatPart {
    type: string;
    pattern: string;
    hour12?: boolean;
    names?: {
        type: string;
        nameType: string;
        standAlone: boolean;
    };
}

/**
 * Options for date format names
 */
export interface DateFormatNamesOptions {
    type: string;
    nameType: string;
}

/**
 * Options for date field name
 */
export interface DateFieldNameOptions {
    type: string;
    nameType: string;
}

/**
 * Interface for date utility service
 */
export interface IDateUtilsService {
    /** Milliseconds per minute constant */
    readonly MS_PER_MINUTE: number;
    /** Milliseconds per hour constant */
    readonly MS_PER_HOUR: number;
    /** Milliseconds per day constant */
    readonly MS_PER_DAY: number;

    /**
     * Adjust date for DST changes
     */
    adjustDST(date: Date, hours: number): boolean;

    /**
     * Set the day of week on a date (mutates the date)
     */
    setDayOfWeek(date: Date, day: number, dir?: number): void;

    /**
     * Get a new date set to the specified day of week
     */
    dayOfWeek(date: Date, day: number, dir?: number): Date;

    /**
     * Get the first day of the month
     */
    firstDayOfMonth(date: Date): Date;

    /**
     * Get the last day of the month
     */
    lastDayOfMonth(date: Date): Date;

    /**
     * Get the first day of the year
     */
    firstDayOfYear(date: Date): Date;

    /**
     * Get the last day of the year
     */
    lastDayOfYear(date: Date): Date;

    /**
     * Get the week number in the year
     */
    weekInYear(date: Date, weekStartDay?: number): number;

    /**
     * Get a date with time set to midnight
     */
    getDate(date: Date): Date;

    /**
     * Convert date to UTC time
     */
    toUtcTime(date: Date): number;

    /**
     * Get milliseconds from midnight
     */
    getMilliseconds(date: Date): number;

    /**
     * Check if a time value is within a range
     */
    isInTimeRange(value: Date | null, min: Date, max: Date): boolean;

    /**
     * Check if a date value is within a range
     */
    isInDateRange(value: Date, min: Date, max: Date): boolean;

    /**
     * Add days to a date
     */
    addDays(date: Date, offset: number): Date;

    /**
     * Set time on a date (mutates the date)
     */
    setTime(date: Date, milliseconds: number, ignoreDST?: boolean): void;

    /**
     * Set hours from another date/time
     */
    setHours(date: Date, time: Date): Date;

    /**
     * Get today's date (midnight)
     */
    today(): Date;

    /**
     * Check if a date is today
     */
    isToday(date: Date): boolean;

    /**
     * Convert date to invariant time (fixed date, preserves time)
     */
    toInvariantTime(date?: Date): Date;

    /**
     * Get the next day
     */
    nextDay(date: Date): Date;

    /**
     * Get the previous day
     */
    previousDay(date: Date): Date;

    /**
     * Get the next year
     */
    nextYear(date: Date): Date;

    /**
     * Get the previous year
     */
    previousYear(date: Date): Date;

    /**
     * Split a date format string into parts
     */
    splitDateFormat(format: string): DateFormatPart[];

    /**
     * Get date format names (month names, day names, etc.)
     */
    dateFormatNames(options: DateFormatNamesOptions): string[];

    /**
     * Get date field name
     */
    dateFieldName(options: DateFieldNameOptions): string | undefined;

    /**
     * Generates a relative date string (Today, Yesterday, Last Wednesday, etc.)
     * @param date - The date to compare against
     * @param currentDate - The current date (defaults to new Date())
     * @returns Relative date string
     */
    getRelativeDateString(date: Date | null | undefined, currentDate?: Date): string;
}
